/**
 * @fileoverview Sentry integration for DiagnosticsModule error tracking.
 *
 * This module provides a custom Sentry integration that bridges unhandled
 * exceptions captured by Sentry with the application's DiagnosticsModule.
 * It enables the diagnostics UI to display errors that Sentry captures
 * automatically (e.g., uncaught exceptions, promise rejections).
 *
 * @module SentryErrorIntegration
 */

import type { Integration, StackFrame } from '@sentry/core';
import type { Event, EventHint } from '@sentry/react';
import AuthModule from '../../AuthModule';
import { TrackErrorInput, TrackedErrorPriority } from '../lib/types';

const INTEGRATION_NAME = 'SentryErrorIntegration';

/**
 * Creates a Sentry integration that tracks unhandled errors through DiagnosticsModule.
 *
 * This integration hooks into Sentry's event pipeline to:
 *
 * 1. **Preprocess events** (`preprocessEvent`): Before Sentry processes an exception,
 *    this hook routes the error to DiagnosticsModule so it appears in the diagnostics UI.
 *    It also applies priority detection logic:
 *    - Errors originating from `src/pages/` are marked as "passive" priority
 *    - Errors already handled by `trackError` are skipped to prevent duplicates
 *
 * 2. **Process events** (`processEvent`): Enriches exception events with user context
 *    by adding `userId` and `service` tags from the authenticated user. This improves
 *    error triage in the Sentry dashboard.
 *
 * @example
 * ```typescript
 * // Usage in Sentry.init() via DiagnosticsModule
 * Sentry.init({
 *   dsn: '...',
 *   integrations: [
 *     DiagnosticsModule.sentryIntegration(),
 *   ],
 * });
 * ```
 *
 * @param trackErrorFn - Callback function to report errors to DiagnosticsModule.
 *                       This is typically the internal `trackError` function from
 *                       DiagnosticsModule that handles error state management.
 *
 * @returns A Sentry {@link Integration} object with `preprocessEvent` and `processEvent` hooks.
 *
 * @see {@link https://docs.sentry.io/platforms/javascript/configuration/integrations/custom-integrations/ | Sentry Custom Integrations}
 */
export function sentryErrorIntegration(
  trackErrorFn: (input: TrackErrorInput) => void,
): Integration {
  return {
    name: INTEGRATION_NAME,

    /**
     * Preprocesses exception events before Sentry's main event processing.
     *
     * This hook runs early in the pipeline and is used to route unhandled
     * exceptions to DiagnosticsModule. It performs the following:
     *
     * 1. Extracts the error message and stack frames from the exception
     * 2. Checks if the error was already handled by DiagnosticsModule (to prevent duplicates)
     * 3. Determines priority based on error origin (page errors are "passive")
     * 4. Reports the error to DiagnosticsModule via the trackErrorFn callback
     */
    preprocessEvent(event: Event, _hint: EventHint): void {
      if (event.exception) {
        let priority: TrackedErrorPriority = 'normal';
        let message = 'Unhandled error';
        let frames: StackFrame[] = [];

        event.exception?.values?.forEach((value) => {
          message = value.value ?? 'Unknown error';
          frames = value.stacktrace?.frames ?? [];

          if (frames.length > 0) {
            // Skip errors already handled by DiagnosticsModule to prevent duplicates.
            // If trackError appears in the stack, the error was explicitly reported.
            const handled = frames.some((frame) =>
              frame.function?.includes('trackError'),
            );
            if (handled) {
              return;
            }

            // Errors from page components are marked as "passive" priority since
            // they're often caught by error boundaries and less actionable.
            const lastFrame = frames[frames.length - 1];
            if (lastFrame?.filename?.includes('src/pages')) {
              priority = 'passive';
            }
          }

          trackErrorFn({
            message,
            priority,
            source: 'sentry',
            errorType: 'unhandled',
            extra: {
              frames,
            },
          });
        });
      }
    },

    /**
     * Processes exception events before they are sent to Sentry.
     *
     * This hook enriches events with user context by adding tags for the
     * authenticated user's ID and contact center service ID. These tags
     * enable filtering and grouping in the Sentry dashboard.
     */
    processEvent(event: Event, _hint: EventHint): Event {
      if (event.exception) {
        const user = AuthModule.user;
        if (user) {
          if (!event.tags) {
            event.tags = {};
          }
          event.tags['userId'] = user.id;
          event.tags['service'] = user.contactCenterId;
        }
      }
      return event;
    },
  };
}
